import {
  CreditRequestByInvoice_SearchVariantPacksQuery,
  CreditRequestByInvoice_SearchVariantPacksQueryVariables,
} from '@/types';
import { SEARCH_VP_FOR_CREDIT_REQUEST_BY_INVOICE_QUERY } from '@/hooks/credit-requests/credit-request-queries';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

interface Params {
  orderInvoiceUUID: string | undefined;
  restaurantUUID: string;
  skip?: boolean;
  supplierUUID: string;
}

/**
 * Fetches credit-request-eligible variant packs for the given order invoice.
 *
 * Used by every screen that renders `CreditRequestInvoiceItemsContent` (the
 * desktop combined payments sheet, the mobile short-pay items screen, and the
 * standalone CR invoice items screen) so the fetch logic and skip rules stay
 * in one place.
 *
 * @param params.orderInvoiceUUID - The UUID of the order invoice to look up
 *   creditable items for. When `undefined` the query is skipped.
 * @param params.restaurantUUID - The restaurant the credit request will be
 *   filed against.
 * @param params.supplierUUID - The supplier the credit request will be filed
 *   against.
 * @param params.skip - Additional skip flag the caller can flip on (e.g. when
 *   a feature gate is off). The query is also skipped automatically when
 *   `orderInvoiceUUID` is unavailable, regardless of this value.
 * @returns `items` is `undefined` while the query is in flight (callers
 *   typically forward this directly to `CreditRequestInvoiceItemsContent`,
 *   which renders a loading indicator on `undefined`); once resolved, it is
 *   always an array (possibly empty). `loading` mirrors Apollo's loading
 *   state.
 *
 * @example
 * const { items, loading } = useCreditRequestInvoiceItems({
 *   orderInvoiceUUID,
 *   restaurantUUID,
 *   supplierUUID,
 * });
 * return (
 *   <CreditRequestInvoiceItemsContent items={items} loading={loading} />
 * );
 */
export const useCreditRequestInvoiceItems = ({
  orderInvoiceUUID,
  restaurantUUID,
  skip,
  supplierUUID,
}: Params) => {
  const itemQueryVariables = useItemQueryVariables();
  const { data, loading } = useWrappedQuery<
    CreditRequestByInvoice_SearchVariantPacksQuery,
    CreditRequestByInvoice_SearchVariantPacksQueryVariables
  >(SEARCH_VP_FOR_CREDIT_REQUEST_BY_INVOICE_QUERY, {
    skip: skip || !orderInvoiceUUID,
    variables: {
      ...itemQueryVariables,
      code: '',
      orderInvoiceUUID: orderInvoiceUUID ?? '',
      restaurantUUID,
      supplierUUID,
    },
  });

  return {
    items: data?.searchVariantPacksForCreditRequest,
    loading,
  };
};
